import {
  Badge,
  Button,
  Dialog,
  type ButtonVariant,
  formatDateTime,
  formatList,
  formatMoney,
  formatTimeAgo,
  orderStatusTone,
  paymentStatusTone,
} from '@platform/design-system';
import { errorMessageKey, useTranslation } from '@platform/i18n';
import type { ReactNode } from 'react';
import { EntryList } from '../EntryList.tsx';
import type { GeoNames } from '../geography/useGeoAreas.ts';
import { amountOf, type Order, type OrderDetail, type OrderHistoryEntry } from './orders.ts';
import type { AvailableOrderAction, OrderActionKey } from './useAvailableOrderActions.ts';
import styles from './Orders.module.css';

export type OrderSheetProps = {
  detail: OrderDetail;
  depotName: string;
  companyName: string | null;
  geoNames: GeoNames;
  carrierName: (code: string) => string;
  available: readonly AvailableOrderAction[];
  labelAction: ReactNode;
  busy: boolean;
  onAction: (key: OrderActionKey, order: Order) => void;
  onClose: () => void;
};

const VARIANT_OF_ACTION: Readonly<Record<OrderActionKey, ButtonVariant>> = {
  cancel: 'ghost',
  edit: 'outline',
  reopen: 'outline',
  dispatch: 'accent',
  confirm: 'primary',
};

export const OrderSheet = ({
  detail,
  depotName,
  companyName,
  geoNames,
  carrierName,
  available,
  labelAction,
  busy,
  onAction,
  onClose,
}: OrderSheetProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { order, history } = detail;
  const money = (amount: string) => formatMoney(amountOf(order, amount), locale);
  const { dispatch } = order;
  const carrierStatusLine =
    dispatch.carrierStatus === null || dispatch.carrierStatusAt === null
      ? null
      : t('orders.carrierStatusLine', {
          status: dispatch.carrierStatus,
          when: formatTimeAgo(dispatch.carrierStatusAt, locale, new Date()),
        });
  const historyLabel = (entry: OrderHistoryEntry): string => {
    if (entry.kind === 'amended')
      return t('orders.historyAmended', {
        fields: formatList(
          entry.changedFields.map((field) => t(`orders.field_${field}`)),
          locale,
        ),
      });
    if (entry.kind === 'payment')
      return t('orders.historyPayment', { status: t(`orders.payment_${entry.reason ?? ''}`) });
    if (entry.fromStatus === null) return t('orders.historyCreated');
    const status = t(`orders.status_${entry.toStatus}`);
    return entry.reason === null ? status : `${status} · ${t(`orders.reason_${entry.reason}`)}`;
  };
  const actions = (
    <>
      {available.toReversed().map(({ key, labelKey, Icon }) => (
        <Button
          key={key}
          icon={<Icon size={20} aria-hidden="true" />}
          variant={VARIANT_OF_ACTION[key]}
          disabled={busy}
          onClick={() => {
            onAction(key, order);
          }}
        >
          {t(labelKey)}
        </Button>
      ))}
    </>
  );

  return (
    <Dialog
      open
      variant="side"
      title={order.number}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={actions}
    >
      <div className={styles['badges']}>
        <Badge tone={orderStatusTone[order.status]}>{t(`orders.status_${order.status}`)}</Badge>
        <Badge tone={paymentStatusTone[order.paymentStatus]}>
          {t(`orders.payment_${order.paymentStatus}`)}
        </Badge>
        {order.isExchange ? <Badge tone="info">{t('orders.exchange')}</Badge> : null}
      </div>
      {dispatch.failure === null ? null : (
        <p className={styles['failure']} role="alert">
          {t('orders.dispatchFailed', { reason: t(errorMessageKey(dispatch.failure)) })}
          {dispatch.failureDetail === null ? null : (
            <span className={styles['carrierMessage']}>
              {t('orders.carrierMessage', { message: dispatch.failureDetail })}
            </span>
          )}
        </p>
      )}
      {order.piiVisible ? null : (
        <p className={styles['notice']} role="status">
          {t('orders.masked')}
        </p>
      )}
      <section aria-label={t('orders.customer')} className={styles['section']}>
        <h3>{t('orders.customer')}</h3>
        <EntryList
          labelOf={(key) => t(`orders.${key}`)}
          entries={[
            { key: 'customer', value: order.customer.name },
            { key: 'phone', value: order.customer.phone, ltr: true },
            { key: 'source', value: order.source === null ? null : t(`orders.source_${order.source}`) },
          ]}
        />
      </section>
      <section aria-label={t('orders.delivery')} className={styles['section']}>
        <h3>{t('orders.delivery')}</h3>
        <EntryList
          labelOf={(key) => t(`orders.${key}`)}
          entries={[
            { key: 'address', value: order.shipping.address },
            { key: 'governorate', value: geoNames(order.shipping.governorate) },
            { key: 'delegation', value: geoNames(order.shipping.delegation) },
            { key: 'postalCode', value: order.shipping.postalCode },
            { key: 'company', value: companyName },
            { key: 'depot', value: depotName },
            { key: 'note', value: order.note },
          ]}
        />
      </section>
      {dispatch.carrierCode === null ? null : (
        <section aria-label={t('orders.shipment')} className={styles['section']}>
          <h3>{t('orders.shipment')}</h3>
          <EntryList
            labelOf={(key) => t(`orders.${key}`)}
            entries={[
              { key: 'carrier', value: carrierName(dispatch.carrierCode) },
              {
                key: 'trackingNumber',
                value: dispatch.trackingNumber ?? t('orders.trackingPending'),
                trackingCode: dispatch.trackingNumber !== null,
              },
              ...(dispatch.carrierReference === null
                ? []
                : [{ key: 'carrierReference', value: dispatch.carrierReference, trackingCode: true }]),
              { key: 'carrierStatus', value: carrierStatusLine },
            ]}
          />
          {dispatch.trackingNumber === null ? null : labelAction}
        </section>
      )}
      <section aria-label={t('orders.lines')} className={styles['section']}>
        <h3>{t('orders.lines')}</h3>
        <ul className={styles['lines']}>
          {order.lines.map((line) => (
            <li key={line.variantId} className={styles['line']}>
              <span>
                {line.productName} <span className={styles['sku']}>{line.sku}</span>
              </span>
              <span className={styles['amount']}>
                {line.quantity} × {money(line.unitPrice)} = {money(line.lineTotal)}
              </span>
            </li>
          ))}
        </ul>
        <dl className={styles['totals']}>
          {(['itemsSubtotal', 'shippingFee', 'codAmount'] as const).map((key) => (
            <div key={key} className={styles['total']}>
              <dt>{t(`orders.${key}`)}</dt>
              <dd className={styles['amount']}>{money(order.totals[key])}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-label={t('orders.history')} className={styles['section']}>
        <h3>{t('orders.history')}</h3>
        <ol className={styles['history']}>
          {history.map((entry, index) => (
            <li key={`${entry.occurredAt}-${String(index)}`}>
              <span>{historyLabel(entry)}</span>
              <span className={styles['muted']}>{formatDateTime(entry.occurredAt, locale)}</span>
            </li>
          ))}
        </ol>
      </section>
    </Dialog>
  );
};
